import { ReactNode, useMemo, useState } from 'react';
import { Download } from 'lucide-react';
import { toCsv, downloadFile } from '../../utils/csv';

export interface Column<T> {
  key: string;
  label: string;
  /** Right-align and use tabular figures. */
  numeric?: boolean;
  render?: (row: T) => ReactNode;
  /** The value sorted on and exported; defaults to row[key]. */
  value?: (row: T) => string | number | null;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  /** CSV file name, without extension. Omit to hide the export button. */
  exportName?: string;
  empty?: string;
  rowKey?: (row: T, index: number) => string | number;
  initialSort?: { key: string; direction: 'asc' | 'desc' };
}

/** A sortable table with CSV export of exactly the rows it shows. */
function DataTable<T extends Record<string, any>>({
  columns,
  rows,
  exportName,
  empty = 'Nothing in this period.',
  rowKey,
  initialSort,
}: DataTableProps<T>) {
  const [sort, setSort] = useState(initialSort || null);

  const valueOf = (column: Column<T>, row: T) => (column.value ? column.value(row) : row[column.key]);

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const column = columns.find((c) => c.key === sort.key);
    if (!column) return rows;
    const factor = sort.direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = valueOf(column, a);
      const vb = valueOf(column, b);
      if (va === vb) return 0;
      if (va === null || va === undefined) return 1;
      if (vb === null || vb === undefined) return -1;
      return (va < vb ? -1 : 1) * factor;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sort, columns]);

  const exportCsv = () => {
    const csvRows = sorted.map((row) => Object.fromEntries(columns.map((c) => [c.key, valueOf(c, row)])));
    downloadFile(`${exportName}.csv`, toCsv(columns, csvRows));
  };

  const toggleSort = (key: string) =>
    setSort((s) => (s && s.key === key ? { key, direction: s.direction === 'asc' ? 'desc' : 'asc' } : { key, direction: 'desc' }));

  return (
    <div className="stats-table-wrap">
      {exportName && rows.length > 0 && (
        <div className="stats-table-actions">
          <button type="button" className="stats-button stats-button-quiet" onClick={exportCsv}>
            <Download size={14} aria-hidden="true" /> Export CSV
          </button>
        </div>
      )}
      {rows.length === 0 ? (
        <p className="stats-empty">{empty}</p>
      ) : (
        <div className="stats-table-scroll">
          <table className="stats-table">
            <thead>
              <tr>
                {columns.map((c) => (
                  <th
                    key={c.key}
                    className={c.numeric ? 'numeric' : undefined}
                    aria-sort={sort?.key === c.key ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    <button type="button" onClick={() => toggleSort(c.key)}>
                      {c.label}
                      {sort?.key === c.key && <span aria-hidden="true">{sort.direction === 'asc' ? ' ▲' : ' ▼'}</span>}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((row, i) => (
                <tr key={rowKey ? rowKey(row, i) : i}>
                  {columns.map((c) => (
                    <td key={c.key} className={c.numeric ? 'numeric' : undefined}>
                      {c.render ? c.render(row) : (valueOf(c, row) ?? '—')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default DataTable;
